import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LOGO_URL = '/assets/hero/logo.png';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Helmet>
        <title>Terms of Service | Ourmend</title>
        <meta name="description" content="Ourmend Terms of Service - rules and guidelines for using our healer marketplace." />
      </Helmet>
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="Ourmend Logo" className="h-12 w-12 object-contain" />
            <div>
              <span className="text-2xl font-bold" style={{color: '#8B4513'}}>Ourmend</span>
              <p className="text-xs text-stone-600 font-bold">Community Healers. Cultural Care.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-8" style={{color: '#3E2723'}}>Terms of Service</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

        <div className="prose max-w-none space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>1. Acceptance of Terms</h2>
            <p>
              By accessing or using Ourmend ("Platform," "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>2. Description of Service</h2>
            <p>
              Ourmend is a marketplace platform connecting individuals seeking healing services ("Patients") with healing practitioners ("Healers"). We facilitate bookings, payments, and communications but do not provide medical or healing services directly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>3. User Accounts</h2>
            <p className="font-semibold mb-2">Account Creation:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must provide accurate, current, and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>You must be at least 18 years old to use this Platform</li>
              <li>One person or entity may not maintain multiple accounts</li>
            </ul>

            <p className="font-semibold mb-2 mt-4">Account Termination:</p>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent, abusive, or illegal activity.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>4. Healer Responsibilities</h2>
            <p>If you register as a Healer, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate information about your qualifications, credentials, and services</li>
              <li>Maintain all necessary licenses and certifications required by law</li>
              <li>Provide services professionally and ethically</li>
              <li>Comply with applicable laws and regulations</li>
              <li>Honor confirmed appointments or provide adequate notice of cancellation</li>
              <li>Maintain appropriate liability insurance</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>5. Patient Responsibilities</h2>
            <p>If you use our Platform as a Patient, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate health information relevant to your appointment</li>
              <li>Honor confirmed appointments or cancel with adequate notice</li>
              <li>Pay for services as agreed</li>
              <li>Treat healers with respect and professionalism</li>
              <li>Consult with appropriate medical professionals for serious health conditions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>6. Bookings and Payments</h2>
            <p className="font-semibold mb-2">Booking Process:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Patients can book appointments through our Platform</li>
              <li>Bookings are confirmed upon payment completion</li>
              <li>Prices are set by individual Healers</li>
            </ul>

            <p className="font-semibold mb-2 mt-4">Payment Processing:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Payments are processed securely through Stripe</li>
              <li>All fees are charged in USD</li>
              <li>We may charge platform fees for our services</li>
            </ul>

            <p className="font-semibold mb-2 mt-4">Cancellation Policy:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Patients may cancel bookings according to the Healer's cancellation policy</li>
              <li>Refunds are subject to the terms set by individual Healers</li>
              <li>We reserve the right to issue refunds in cases of service disputes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>7. Medical Disclaimer</h2>
            <p className="font-semibold mb-2">IMPORTANT NOTICE:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Ourmend is a marketplace platform, not a medical provider</li>
              <li>We do not provide medical advice, diagnosis, or treatment</li>
              <li>Services offered by Healers may be complementary or alternative practices</li>
              <li>Healers are independent contractors, not employees of Ourmend</li>
              <li>Always consult licensed medical professionals for serious health concerns</li>
              <li>We do not verify the efficacy of healing modalities offered</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>8. Liability and Disclaimers</h2>
            <p className="font-semibold mb-2">Platform Use:</p>
            <p>
              THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <p className="font-semibold mb-2 mt-4">Limitation of Liability:</p>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, OURMEND SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM OR SERVICES OBTAINED THROUGH THE PLATFORM.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>9. User Conduct</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Platform for illegal purposes</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post false, misleading, or fraudulent information</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with Platform operation or security</li>
              <li>Scrape or data mine the Platform</li>
              <li>Impersonate another person or entity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>10. Intellectual Property</h2>
            <p>
              All content on the Platform, including text, graphics, logos, and software, is the property of Ourmend or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>11. Privacy</h2>
            <p>
              Your use of the Platform is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect, use, and protect your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>12. Dispute Resolution</h2>
            <p>
              Any disputes arising from these Terms or your use of the Platform shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. You waive your right to participate in class action lawsuits.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant changes. Continued use of the Platform after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{color: '#8B4513'}}>15. Contact Information</h2>
            <p>
              For questions about these Terms, please contact us:
            </p>
            <ul className="list-none space-y-1 mt-4">
              <li><strong>Email:</strong> kgracedevelopment@gmail.com</li>
              <li><strong>Platform:</strong> Ourmend</li>
            </ul>
          </section>

          <section className="mt-8 p-6 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm">
              By using Ourmend, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;